import { useLanguage } from "@/context/language"
import { updateWorkspaceSettings, listWorkspaceAgents } from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { SettingsListV2 } from "./parts/list"
import { SettingsRowV2 } from "./parts/row"
import { createEffect, createMemo, createResource, Show } from "solid-js"
import { createStore } from "solid-js/store"

export const COWORK_DEFAULT_AGENT_KEY = "cowork.agent.defaultChatAgentId"

export type CoworkDefaultAgentID = "custom-report" | "accounting"

export function CoworkAgentsSettings() {
  const language = useLanguage()
  const workspace = useCoworkWorkspace()
  const [assigned] = createResource(listWorkspaceAgents)
  const [store, setStore] = createStore({ defaultAgentID: "accounting" as CoworkDefaultAgentID, saving: false, error: false })
  const agents = createMemo(() => coworkDefaultAgentOptions(language))

  createEffect(() => {
    const value = workspace.workspaceSettings()?.default_agent_id
    if (value === "accounting" || value === "custom-report") {
      setStore("defaultAgentID", value)
      return
    }
    const match = assigned()?.find((agent) => agent.id === value)
    if (match?.template_id === "accounting" || match?.template_id === "custom-report") {
      setStore("defaultAgentID", match.template_id)
    }
  })

  const setDefaultAgent = async (agentID: CoworkDefaultAgentID) => {
    const previous = store.defaultAgentID
    setStore({ defaultAgentID: agentID, saving: true, error: false })
    try {
      await updateWorkspaceSettings({ default_agent_id: agentID })
      await workspace.refreshWorkspaceSettings()
      localStorage.setItem(COWORK_DEFAULT_AGENT_KEY, agentID)
      window.dispatchEvent(new CustomEvent("cowork:default-agent-changed", { detail: { agentID } }))
    } catch {
      setStore({ defaultAgentID: previous, error: true })
    } finally {
      setStore("saving", false)
    }
  }

  return (
    <>
      <div class="settings-v2-tab-header">
        <h2 class="settings-v2-tab-title">{language.t("cowork.settings.agents.title")}</h2>
        <p class="settings-v2-tab-description">{language.t("cowork.settings.agents.description")}</p>
      </div>

      <SettingsListV2>
        <SettingsRowV2 title={language.t("cowork.settings.agents.default.title")} description={language.t("cowork.settings.agents.default.description")}>
          <select
            class="h-9 min-w-[220px] rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular text-v2-text-text-base outline-none"
            value={store.defaultAgentID}
            disabled={store.saving || workspace.workspaceSettings.loading}
            onChange={(event) => void setDefaultAgent(event.currentTarget.value as CoworkDefaultAgentID)}
          >
            {agents().map((agent) => <option value={agent.id}>{agent.name}</option>)}
          </select>
          <Show when={store.error}>
            <p role="alert" class="text-v2-state-fg-danger">{language.t("cowork.enterprise.settings.error")}</p>
          </Show>
        </SettingsRowV2>
      </SettingsListV2>
    </>
  )
}

export function storedDefaultAgentID(): string {
  const value = localStorage.getItem(COWORK_DEFAULT_AGENT_KEY)
  return value === "accounting" || value === "custom-report" ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value ?? "")
    ? value! : "accounting"
}

export function coworkDefaultAgentOptions(language: ReturnType<typeof useLanguage>) {
  return [
    {
      id: "custom-report" as const,
      name: language.t("cowork.agents.template.customReport"),
      description: language.t("cowork.agents.template.customReport.description"),
    },
    {
      id: "accounting" as const,
      name: language.t("cowork.agents.template.accounting"),
      description: language.t("cowork.agents.template.accounting.description"),
    },
  ]
}
